import { useState } from "react";

import { useTranslation } from "react-i18next";

import { ApiError } from "@/api/client";
import { useResetUserPassword, type User } from "@/api/users";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import type { Secret } from "./TemporaryPasswordDialog";

interface Props {
  user: User | null;
  onClose: () => void;
  onReset: (secret: Secret) => void;
}

export function ResetPasswordDialog({ user, onClose, onReset }: Props) {
  const { t } = useTranslation();
  const reset = useResetUserPassword();
  const [serverError, setServerError] = useState<string | null>(null);

  if (!user) return null;

  const close = () => {
    setServerError(null);
    onClose();
  };

  const handleReset = async () => {
    setServerError(null);
    try {
      const res = await reset.mutateAsync(user.id);
      onReset({ email: user.email, password: res.temporaryPassword });
      close();
    } catch (err) {
      setServerError(
        (err instanceof ApiError && err.error?.message) ||
          t("settings.users.error"),
      );
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && close()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("settings.users.resetPasswordTitle")}</DialogTitle>
          <DialogDescription>
            {t("settings.users.resetPasswordConfirmation")}
          </DialogDescription>
        </DialogHeader>
        {serverError && (
          <p className="text-destructive text-sm">{serverError}</p>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={close}>
            {t("common.buttons.cancel")}
          </Button>
          <Button disabled={reset.isPending} onClick={handleReset}>
            {t("settings.users.resetPassword")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
